import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
    createNewTest,
    getTest,
    getTestComponentsForLevel,
    updateTest,
    getTests,
} from '../../../apiClients/backendApi';
import useTitle from '../../../systems/TitleManager/useTitle';
import { useLevels } from '@dyl/systems/LevelsManager';
import { without } from 'lodash';
import { useAuth } from '@dyl/systems/UserManager';

/**
 * @template T
 * @typedef {[T, (next: T | (prev: T) => T) => void]} UseStateReturn
 */

/**
 * @typedef ComponentThreshold
 * @property {string} test_id
 * @property {number[]} shields
 */

/**
 * @typedef FocusThreshold
 * @property {number} threshold
 * @property {string} tf_id
 * @property {string} tag
 * @property {string} name
 * @property {string} qpvSet
 * @property {string} strengths_in_english
 * @property {string} here_are_some_things
 * @property {string} what_can_i_do
 */

/**
 * @typedef ComponentOption
 * @property {string} test_id
 * @property {boolean} is_published
 * @property {string} test_name
 * @property {'speaking' | 'reading_writing' | 'listening'} skill_id
 */

export default function useLogic({ isNew = false }) {
    const navigate = useNavigate();
    const { user } = useAuth();

    /** @type {{ level?: string, testId?: string }} */
    const params = useParams();
    const { enqueueSnackbar } = useSnackbar();

    // If not new (edit mode), is initially loading, as test must be loaded.
    const [isLoading, setLoading] = useState(!isNew);

    const { levels, globalLevel } = useLevels();

    /** @type {UseStateReturn<ComponentOption[]>} */
    const [componentOptions, setComponentOptions] = useState([]);

    const [name, setName] = useState('');
    const [originalName, setOriginalName] = useState('');
    const [status, setStatus] = useState('draft');
    const [level, setLevel] = useState(params.level ?? globalLevel.level_id);
    const [qpvSet, setQpvSet] = useState(undefined);
    /** @type {UseStateReturn<ComponentOption[]>} */
    const [selectedComponentIds, setSelectedComponentIds] = useState([]);
    const [nameError, setNameError] = useState('');
    const [checkingUnique, setCheckingUnique] = useState(false);
    const [existingTests, setExistingTests] = useState([]);

    const selectedComponents = useMemo(() => {
        return selectedComponentIds.map(id => {
            return componentOptions.find(c => c.test_id === id);
        });
    }, [selectedComponentIds, componentOptions]);

    const hasContent = selectedComponents.length > 0;

    const [isSaving, setSaving] = useState(false);

    const checkNameUnique = useCallback(async (candidateName) => {
        // Only enforce uniqueness when creating a new test
        if (!isNew) {
            setNameError('');
            return true;
        }
        if (!level || !candidateName) {
            setNameError('');
            return true;
        }
        // Use cached list for instant validation
        const normalized = String(candidateName).trim().toLowerCase();
        const isDuplicate = existingTests.some(t => String(t?.name || '').trim().toLowerCase() === normalized);
        setNameError(isDuplicate ? 'Test ID is already in use. Please choose a unique Test ID.' : '');
        return !isDuplicate;
    }, [isNew, level, existingTests]);

    const saveTest = async () => {
        setSaving(true);

        let outputTestId = params.testId;

        try {
            // Pre-check uniqueness for new tests only
            if (isNew) {
                const unique = await checkNameUnique(name);
                if (!unique) {
                    enqueueSnackbar('Test ID is already in use. Please choose a unique Test ID.', { variant: 'error' });
                    setSaving(false);
                    return;
                }
            }
            const component_ids = selectedComponents.map(c => c.test_id);
            const compWithNullQpvRef = selectedComponents.filter(c => c.qpv_reference === null);
            const skillWithNoQpvRef = compWithNullQpvRef.map(c => c.skill_id);

            if ((skillWithNoQpvRef.length > 0) && status === 'live') {
                // Show error message and return without updating the test
                enqueueSnackbar
                    (`Cannot save test when ${skillWithNoQpvRef.join(', ')} have null QPV reference.`,
                    {
                        variant: 'error',
                        autoHideDuration: 20000,
                    }
                );
                setSaving(false);
                return;
            }
            // Stage 1: if new, create test; otherwise, get state values
            const { testId } = await (async () => {
                // For creating...
                if (isNew) {
                    const { test_group_id: testId } = await createNewTest(level, name, qpvSet, component_ids);
                    outputTestId = testId

                    return { testId: testId };
                }
                // For updating...
                else {
                    /** @type {string} */
                    const testId = params.testId;

                    return { testId };
                }
            })();

            // Stage 2: Update the test's content.

            /** @type {string} */
            let username = user?.attributes?.email;
            await updateTest(testId, level, {
                name,
                status,
                qpvSet,
                username,
                test_ids: component_ids,
            });
        }
        catch (error) {
            // Bubble 409 conflict as inline error (creation only)
            const status = error?.response?.status ?? error?.status;
            if (status === 409 && isNew) {
                setNameError('Test ID is already in use. Please choose a unique Test ID.');
            }
            enqueueSnackbar(
                `Failed to ${isNew ? 'create' : 'update'} test`,
                { variant: 'error' }
            );

            console.error(error);

            return;
        }
        finally {
            setSaving(false);
        }

        enqueueSnackbar(
            `Test ${isNew ? 'created' : 'updated'}`,
            { variant: 'success' }
        );

        // Once created, go to the edit screen.
        if (isNew) {
            navigate(`/${globalLevel.label}/tests/${outputTestId}`);
        }
    };

    const title = (
        isLoading ? 'Loading...'
        : isNew ? 'New Test'
        : name || '<NO NAME>'
    );

    useTitle({ breadcrumbs: ['Tests', title] });

    // On level change
    useEffect(() => {
        //TODO: show loading overlay

        // Download level-specific content
        (async () => {
            const components = await getTestComponentsForLevel(level);

            setComponentOptions(components);
        })();
    }, [level]);

    // Preload tests for instant uniqueness checks in New and Edit flows
    useEffect(() => {
        (async () => {
            if (level) {
                try {
                    setCheckingUnique(true);
                    const tests = await getTests(level);
                    setExistingTests(Array.isArray(tests) ? tests : []);
                } catch (e) {
                    setExistingTests([]);
                } finally {
                    setCheckingUnique(false);
                }
            } else {
                setExistingTests([]);
            }
        })();
    }, [level]);

    // Early preload on mount using global level to minimize first-keystroke delay
    useEffect(() => {
        (async () => {
            if (!isNew) return;
            const lvl = level || globalLevel.level_id;
            if (!lvl) return;
            try {
                setCheckingUnique(true);
                const tests = await getTests(lvl);
                setExistingTests(Array.isArray(tests) ? tests : []);
            } catch (e) {
                // ignore
            } finally {
                setCheckingUnique(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const _shouldDownloadTest = !isNew && componentOptions.length > 0;

    // For edit mode, download test once appropriate data ready
    useEffect(() => {
        if (_shouldDownloadTest) {
            (async () => {
                const test = await getTest(globalLevel.level_id, params.testId);

                setName(test.name);
                setOriginalName(test.name);
                setStatus(test.status);
                setQpvSet(test.qpvSet ?? undefined)
                setSelectedComponentIds(test.tests.map(t => t.test_id));

                setLoading(false);
            })();
        }
    }, [_shouldDownloadTest]);

    const onNameChange = useCallback(e => {
        const next = e.target.value;
        setName(next);
        if (nameError) setNameError('');
        // Immediate in-memory uniqueness check for New and Edit (excluding unchanged original)
        const normalized = String(next).trim().toLowerCase();
        const normalizedOriginal = String(originalName || '').trim().toLowerCase();
        if (!isNew && normalized === normalizedOriginal) {
            setNameError('');
        } else {
            const duplicate = existingTests.some(t => String(t?.name || '').trim().toLowerCase() === normalized);
            setNameError(duplicate ? 'Test ID is already in use. Please choose a unique Test ID.' : '');
        }
    }, [nameError, isNew, existingTests, originalName]);

    const onNamePaste = useCallback(e => {
        // Let the paste happen, then validate on the next paint for the updated value
        const target = e.target;
        requestAnimationFrame(() => {
            const value = String(target?.value || '').trim();
            setName(value);
            const normalized = value.toLowerCase();
            const normalizedOriginal = String(originalName || '').trim().toLowerCase();
            if (!isNew && normalized === normalizedOriginal) {
                setNameError('');
            } else {
                const duplicate = existingTests.some(t => String(t?.name || '').trim().toLowerCase() === normalized);
                setNameError(duplicate ? 'Test ID is already in use. Please choose a unique Test ID.' : '');
            }
        });
    }, [isNew, existingTests, originalName]);
    const onNameBlur = useCallback(() => {
        const normalized = String(name).trim().toLowerCase();
        const normalizedOriginal = String(originalName || '').trim().toLowerCase();
        if (!isNew && normalized === normalizedOriginal) {
            setNameError('');
            return;
        }
        const duplicate = existingTests.some(t => String(t?.name || '').trim().toLowerCase() === normalized);
        setNameError(duplicate ? 'Test ID is already in use. Please choose a unique Test ID.' : '');
    }, [isNew, name, existingTests, originalName]);

    // Validate on any input event (covers autofill and programmatic updates)
    const onNameInput = useCallback(e => {
        const value = String(e.target.value || '').trim();
        setName(value);
        const normalized = value.toLowerCase();
        const normalizedOriginal = String(originalName || '').trim().toLowerCase();
        if (!isNew && normalized === normalizedOriginal) {
            setNameError('');
            return;
        }
        const duplicate = existingTests.some(t => String(t?.name || '').trim().toLowerCase() === normalized);
        setNameError(duplicate ? 'Test ID is already in use. Please choose a unique Test ID.' : '');
    }, [isNew, existingTests, originalName]);
    const onLevelChange = useCallback(e => setLevel(e.target.value), []);
    const onStatusChange = useCallback(e => {
        setStatus(e.target.value);
    }, [setStatus]);
    const onQpvSetChange = useCallback(e => {
        let qpvSetValue = e.target.value;
        qpvSetValue = qpvSetValue.trim() === '' ? undefined : qpvSetValue
        setQpvSet(qpvSetValue)
    }, []);

    const removeComponent = useCallback(id => {
        setSelectedComponentIds(prev => without(prev, id));
    }, []);

    // Levels are set globally from the side-nav bar.
    const allowLevelChange = false; // isNew && !hasContent && levelsStatus === 'complete';
    const allowContentEditing = !!level;
    const allowAddContent = componentOptions.length > 0;
    const allowSaving = !!name && !isSaving && hasContent && !nameError;

    return {
        isNew,
        isLoading,

        levels,
        level,
        onLevelChange,
        allowLevelChange,

        name,
        onNameChange,
        onNameBlur,
        onNamePaste,
        onNameInput,

        allowContentEditing,
        allowAddContent,
        componentOptions,

        selectedComponents,
        selectedComponentIds,
        setSelectedComponentIds,
        removeComponent,

        allowSaving,
        saveTest,
        isSaving,
        nameError,
        checkingUnique,

        qpvSet,
        onQpvSetChange,

        status,
        onStatusChange,
    };
}